import { cx } from '@emotion/css';
import { PlaceTwoTone } from '@mui/icons-material';
import {
  COLORS,
  CustomClearIndicator,
  CustomRemoveIndicator,
  Option,
  RADIUSES,
  SHADOWS,
  Skeleton,
  TYPOGRAPHY,
} from '@sortlist-frontend/design-system/legacy';
import { isBrowser } from '@sortlist-frontend/utils';
import { ReactNode, useEffect, useRef, useState } from 'react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import GooglePlacesAutocompleteProps, {
  GooglePlacesAutocompleteHandle,
} from 'react-google-places-autocomplete/build/types';
import { ControlProps, GroupBase, MenuListProps, MenuProps, OnChangeValue, OptionProps } from 'react-select';

import { useGooglePlacesApi } from '../hooks';
import { IconControl, IconOption } from '../Select';
import * as S from './styles';
import {
  getExtendedDefaultLocation,
  getExtendedLocation,
  GooglePlaceAutocompleteResponse,
  isLocation,
  LocationType,
  Menu,
  MenuList,
  valueComposer,
} from './utils';

export type { GooglePlacesAutocompleteProps, GooglePlacesAutocomplete, GooglePlacesAutocompleteHandle };

export type LocationAutocompleteProps = {
  error?: string;
  types?: string[];
  placeholder: ReactNode | string;
  lang?: string;
  onChange: (location?: LocationType | LocationType[]) => void;
  currentValue?: LocationType | LocationType[];
  minHeight?: string;
  controlBorder?: string;
  controlBackgroundColor?: string;
  className?: string;
  icon?: ReactNode;
  showAnywhere?: boolean;
  anywhereLabel?: string;
  selectProps?: GooglePlacesAutocompleteProps['selectProps'];
  onBlur?: () => void;
  onFocus?: () => void;
  isMulti?: boolean;
};

/**
 * @deprecated Use LocationAutocomplete instead
 */
export const LocationAutocompleteLegacy = (props: LocationAutocompleteProps) => {
  const {
    onChange,
    isMulti = false,
    lang = 'en',
    placeholder,
    currentValue,
    types,
    minHeight,
    controlBorder,
    controlBackgroundColor,
    className,
    icon,
    showAnywhere = false,
    anywhereLabel,
    onBlur,
    error,
    onFocus,
    ...restProps
  } = props;

  const [value, setValue] = useState<LocationType | LocationType[] | undefined>();
  const googleRef = useRef(null);
  const { done: isLoaded } = useGooglePlacesApi({ locale: lang, async: false });

  const handleChange = (newLocation: LocationType | LocationType[] | undefined) => {
    setValue(newLocation);
    onChange(newLocation);
  };

  const setLocation = async (
    option: OnChangeValue<{ value: GooglePlaceAutocompleteResponse | LocationType }, boolean>,
  ) => {
    if (isMulti) {
      const locations = option as OnChangeValue<{ value: GooglePlaceAutocompleteResponse | LocationType }, true>;

      if (locations.length === 0) {
        handleChange(undefined);
        return;
      }

      const newLocations = await Promise.all(
        locations.map((loc) => (isLocation(loc.value) ? loc.value : getExtendedLocation(loc.value))),
      );
      handleChange(newLocations);
    } else {
      const location = (option as OnChangeValue<{ value: GooglePlaceAutocompleteResponse | LocationType }, false>)
        ?.value;
      if (location == null) {
        handleChange(undefined);
        return;
      }
      const newLocation = isLocation(location) ? location : await getExtendedLocation(location);
      handleChange(newLocation);
    }
  };

  const getControlBorder = (state: ControlProps<Option, boolean, GroupBase<Option>>) => {
    if (controlBorder) return controlBorder;
    if (error) return `1px solid ${COLORS.DANGER_100}`;
    if (state.isFocused) return `1px solid ${COLORS.SECONDARY_500}`;
    return `1px solid ${COLORS.SECONDARY_300}`;
  };

  const getControlBackground = () => {
    if (controlBackgroundColor) return controlBackgroundColor;
    if (error) return COLORS.DANGER_100;

    return 'rgba(15, 13, 10, 0.04)';
  };

  useEffect(() => {
    const setDefaultLocation = async () => {
      if (currentValue == null) return;

      if (isMulti && Array.isArray(currentValue)) {
        const defaultValues = await Promise.all(currentValue.map((loc) => getExtendedDefaultLocation(loc)));
        handleChange(defaultValues);
      } else if (!isMulti && !Array.isArray(currentValue)) {
        const defaultValue = await getExtendedDefaultLocation(currentValue as LocationType);
        handleChange(defaultValue);
      }
    };

    if (isBrowser() && isLoaded && currentValue != null && value == null) {
      setDefaultLocation();
    }
  }, [isLoaded, currentValue]);

  if (isLoaded === false) return <Skeleton height={40} />;

  return (
    <S.LocationSelectorWrapper data-testid="location-autocomplete" className={cx(className, 'width-100')}>
      <GooglePlacesAutocomplete
        apiOptions={{ language: lang, region: lang }}
        autocompletionRequest={{ types }}
        ref={googleRef}
        debounce={1000}
        key={`location-select-${value ? (value as LocationType).placeId || 'anywhere' : 'not-selected'}`}
        selectProps={{
          instanceId: 'react-select-google-autocomplete',
          isClearable: true,
          autoFocus: false,
          backspaceRemovesValue: false,
          blurInputOnSelect: true,
          classNamePrefix: 'react-select',
          isMulti: isMulti as unknown as undefined,
          value: value
            ? isMulti
              ? (value as LocationType[]).map((v) => valueComposer(v))
              : valueComposer(value as LocationType)
            : undefined,
          getOptionLabel: (option) => option?.label,
          getOptionValue: (option) => option?.value,
          onChange: setLocation,
          onBlur: () => {
            onBlur && onBlur();
          },
          onFocus: () => {
            onFocus && onFocus();
          },
          placeholder,
          styles: {
            control: (provided, state) => ({
              ...provided,
              border: getControlBorder(state),
              boxShadow: 'none',
              cursor: state.isDisabled ? 'not-allowed' : 'text',
              borderTopRightRadius: '0.375rem',
              borderBottomRightRadius: '0.375rem',
              borderTopLeftRadius: '0.375rem',
              borderBottomLeftRadius: '0.375rem',
              backgroundColor: getControlBackground(),
              '&:hover': {
                boxShadow: 'none',
              },
            }),
            placeholder: (provided, state) => ({
              ...provided,
              left: 0,
              right: 0,
              margin: 0,
              width: '100%',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              pointerEvents: 'auto',
              textOverflow: 'ellipsis',
              fontFamily: TYPOGRAPHY.SANS_SERIF,
              opacity: state.isDisabled ? 0.6 : 1,
              padding: '2px 8px',
              color: error ? COLORS.DANGER_500 : COLORS.SECONDARY_500,
            }),
            valueContainer: (provided) => ({
              ...provided,
              padding: '2px 8px',
            }),
            multiValue: (provided) => ({
              ...provided,
              borderRadius: RADIUSES.sm,
              color: COLORS.SECONDARY_900,
              backgroundColor: COLORS.SECONDARY_300,
              '&:hover': {
                color: COLORS.PRIMARY_500,
                backgroundColor: COLORS.PRIMARY_200,
              },
            }),
            multiValueLabel: (provided) => ({
              ...provided,
              borderRadius: 0,
              color: 'inherit',
              padding: '0.25rem',
              paddingLeft: '0.5rem',
              fontSize: TYPOGRAPHY.SMALL,
              fontFamily: TYPOGRAPHY.SANS_SERIF,
            }),
            indicatorsContainer: (provided, state) => ({
              ...provided,
              minHeight: '2.375rem',
              alignSelf: 'start',
              padding: '0 0.5rem',
              color: error ? COLORS.DANGER_500 : state.isDisabled ? COLORS.SECONDARY_300 : COLORS.SECONDARY_700,
            }),
            container: (provided) => ({
              ...provided,
              border: 'none',
              flex: 1,
              borderColor: 'transparent',
            }),
            menu: (provided) => ({
              ...provided,
              border: 'none',
              padding: '0.5rem 0',
              zIndex: 2,
              borderRadius: '0.375rem',
              boxShadow: SHADOWS.DEPTH_5.SECONDARY_900,
            }),
            menuList: (provided) => ({
              ...provided,
            }),
            option: (provided, state) => {
              delete provided.display;

              return {
                ...provided,
                cursor: 'pointer',
                flexDirection: 'row',
                fontFamily: TYPOGRAPHY.SANS_SERIF,
                color: state.isFocused ? COLORS.PRIMARY_500 : COLORS.SECONDARY_900,
                backgroundColor: state.isFocused ? COLORS.PRIMARY_100 : COLORS.NEUTRAL_100,
                '&:active': {
                  backgroundColor: COLORS.PRIMARY_200,
                },
              };
            },

            singleValue: (provided, state) => ({
              ...provided,
              opacity: state.isDisabled ? 0.5 : 1,
              color: error ? COLORS.DANGER_500 : COLORS.SECONDARY_900,
              fontFamily: TYPOGRAPHY.SANS_SERIF,
            }),
          },
          components: {
            Menu: (props) => Menu(props as MenuProps<unknown, false>),
            ...(showAnywhere && anywhereLabel
              ? {
                  MenuList: (props) =>
                    MenuList(props as MenuListProps<unknown, false, GroupBase<unknown>>, handleChange, anywhereLabel),
                }
              : {}),
            Option: (props) => (
              <IconOption
                optionIcon={<PlaceTwoTone style={{ fontSize: 16 }} className="mr-2" />}
                {...(props as OptionProps<Option, boolean>)}
              />
            ),
            ClearIndicator: CustomClearIndicator,
            MultiValueRemove: CustomRemoveIndicator,
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
            NoOptionsMessage: () => null,
            ...(icon
              ? {
                  Control: (props) => (
                    <IconControl icon={icon} {...(props as ControlProps<unknown, false, GroupBase<unknown>>)} />
                  ),
                }
              : {}),
          },
          ...props.selectProps,
        }}
      />
    </S.LocationSelectorWrapper>
  );
};
